import { Button, Card, Chip, HSpacer, Switch, Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import { ManufacturerApi } from '@/utilities/api/ManufacturerApi';
import { ApiManufacturer } from '@api/interfaces';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Box, Stack, useTheme } from '@mui/material';
import React, { Fragment, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { SaveManufacturerModal } from './SaveEditManufacturer';
import { ManufacturerAvatar } from "@/components/DesignSystem/Avatar/ManufacturerAvatar";

const ManufacturerListItem = ({
  manufacturer,
  onClick,
  showActions = true,
  showID = false,
}: {
  manufacturer: ApiManufacturer;
  onClick?: () => void;
  showActions?: boolean;
  showID?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [isEditManufacturer, setIsEditManufacturer] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isActive, setIsActive] = useState(manufacturer.isActive ?? false);
  const theme = useTheme();

  const { mutate: updateManufacturerActivation, isLoading } = useMutation(
    async (isActive: boolean) => {
      return ManufacturerApi.update(manufacturer.id, {
        isActive: isActive,
      });
    },
    {
      onSuccess: async (manufacturer) => {
        await queryClient.invalidateQueries(QueryKeys.GET_MANUFACTURERS);
        setIsActive(manufacturer.isActive ?? false);
        setShowConfirmDialog(false);
      },
    },
  );

  return (
    <Fragment key={manufacturer.id}>
      <Card
        elevation={4}
        {...(!!onClick && { onClick })}
        sx={{ cursor: onClick ? 'pointer' : 'default' }}
        testID='manufacturer-list-item'
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Stack alignItems={showID ? "flex" : "center"} direction="row">
            <ManufacturerAvatar manufacturer={manufacturer} />
            <HSpacer size="5"/>
            <Stack spacing={1}>
              {showID
                ? <>
                    <Text category="headline-medium">{manufacturer?.name}</Text>
                    <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                      ID# {manufacturer.id}
                    </Text>
                  </>
                : <Text category="body-xlarge">{manufacturer?.name}</Text>
              }
            </Stack>
          </Stack>
          {showActions ? (
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <Stack style={{ marginTop: '7px' }}>
                <Switch
                  checked={isActive}
                  label={manufacturer.isActive ? 'Active' : 'Inactive'}
                  labelPlacement="start"
                  onClick={() => {
                    if (!isActive) {
                      updateManufacturerActivation(true);
                    } else {
                      setShowConfirmDialog(true);
                    }
                  }}
                  testID="partner-retailer-list-item-is-inactive"
                />
              </Stack>
              <HSpacer size="7"/>
              <Button
                onClick={() => setIsEditManufacturer(true)}
                startIcon={<EditOutlined/>}
                testID="Manufacturer-Edit-details-button"
                variant="outlined"
              >
                Edit
              </Button>
            </Box>
          ) : (
            <Chip
              color={isActive ? 'success' : undefined}
              label={isActive ? 'ACTIVE' : 'INACTIVE'}
              rounded
              sx={{ height: "auto", paddingLeft: "6px" }}
              testID={isActive ? `${manufacturer.id}-active` : `${manufacturer.id}-inactive`}
            />
          )}
        </Stack>
      </Card>
      <VSpacer size="5"/>
      {
        isEditManufacturer && (
          <SaveManufacturerModal
            close={() => setIsEditManufacturer(false)}
            manufacturer={manufacturer}
            retailerId={manufacturer.retailerId}
          />
        )}
      <ConfirmDialog
        isLoading={isLoading}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          updateManufacturerActivation(false);
          setShowConfirmDialog(false);
        }}
        open={showConfirmDialog}
        testID="update-manufacturer-dialog"
        title='Update Manufacturer'
      >
        Are you sure you want to set this manufacturer to inactive?
      </ConfirmDialog>
    </Fragment>
  );
};

export default ManufacturerListItem;
